import React, { FC, useEffect, useState } from 'react';
import { faBus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Autocomplete,
  AutocompleteRenderInputParams
} from 'formik-material-ui-lab';
import {
  TIPOS_SERVICIO_VIAJE_TRASLADO,
  TipoServicioLabel,
  ViajeTrasladoTipoServicio
} from '../../models';
import { ClientBusinessModel } from 'app/client/clientBusiness/models/ClientBusiness';
import { AuthorizedModel } from 'app/client/clientBusiness/Authorized/models/Authorized';
import { RateModel } from 'app/client/clientBusiness/rates/models/RateModel';
import { BusType } from '../../models/TransferReservation';
import { Query } from '@vadiun/react-hooks-legacy';
import {
  ErrorMessage,
  Field,
  useFormikContext
} from 'formik';
import {
  FormControl,
  InputLabel,
  MenuItem,
  TextField as TextFieldMaterial
} from '@material-ui/core';
import { CheckboxWithLabel, Select, TextField } from 'formik-material-ui';
import { TransferReservationSingleFormType } from './TransferReservationSingleFormType';

interface VehicleSingleFormProps {
  autorizadosQuery: Query<AuthorizedModel[]>;
  clientBusiness: ClientBusinessModel | undefined;
  fixedRates?: any;
  optionsBuses?: BusType[];
  isValidation?: boolean;
}

const VehicleSingleForm: FC<VehicleSingleFormProps> = ({
  autorizadosQuery,
  clientBusiness,
  fixedRates,
  optionsBuses,
  isValidation
}) => {
  const [showBusesFields, setShowBusesFields] = useState<boolean>(false);
  const {
    values,
    setFieldValue,
    errors,
    touched,
    setFieldTouched,
    isSubmitting
  } = useFormikContext<TransferReservationSingleFormType>();

  const [fixedRatesOptions, setFixedRatesOtions] = useState<any[]>([]);

  // Sincronizar showBusesFields con el valor inicial de tipoServicio
  useEffect(() => {
    const currentTipoServicio = values.tipoServicio;
    if (currentTipoServicio === ViajeTrasladoTipoServicio.SERVICIOS_GRUPALES) {
      setShowBusesFields(true);
    } else {
      setShowBusesFields(false);
    }
  }, [values.tipoServicio]);

  useEffect(() => {
    if (errors?.bus_provider_price) {
      setFieldTouched('bus_provider_price', true);
    }
    if (errors?.bus_customer_price) {
      setFieldTouched('bus_customer_price', true);
    }
  }, [isSubmitting]);

  useEffect(() => {
    if (fixedRates?.categories) {
      setFixedRatesOtions(
        fixedRates.categories.find(
          (rate) => rate.name === values.tipoServicio
        )?.fixedPrices || []
      );
    } else {
      setFixedRatesOtions([]);
    }
  }, [fixedRates, values.tipoServicio]);

  return (
    <>
      <div className="grid grid-cols-2 gap-6 border-b-2 border-dashed py-8">
        <FormControl variant="outlined" fullWidth className="col-span-1">
          <InputLabel>Tipo de servicio</InputLabel>
          <Field
            component={Select}
            name="tipoServicio"
            label="Tipo de servicio"
            value={values.tipoServicio ?? ''}
            onChange={(e) => {
              setFieldValue('tipoServicio', e.target.value);
              setFieldValue('fixedRate', null);
              if (
                e.target.value === ViajeTrasladoTipoServicio.SERVICIOS_GRUPALES
              ) {
                setShowBusesFields(true);
                setFieldValue('bus_type_id', 19);
                setFieldValue('bus_customer_price', undefined);
                setFieldValue('bus_provider_price', undefined);
                setFieldValue('bus_is_outsourced', false);
                setFieldValue('bus_observation', undefined);
              } else {
                setShowBusesFields(false);
                setFieldValue('bus_type_id', undefined);
                setFieldValue('bus_customer_price', undefined);
                setFieldValue('bus_provider_price', undefined);
                setFieldValue('bus_is_outsourced', false);
                setFieldValue('bus_observation', undefined);
              }
            }}
            helperText={
              touched.tipoServicio && errors?.tipoServicio
            }
            error={
              errors?.tipoServicio && touched.tipoServicio
            }
          >
            {TIPOS_SERVICIO_VIAJE_TRASLADO.map((canal) => (
              <MenuItem value={canal} key={canal}>
                {TipoServicioLabel[`${canal}`]}
              </MenuItem>
            ))}
          </Field>
          <ErrorMessage name="tipoServicio">
            {(msg) => <p className="mt-4 text-sm text-red-500">{msg}</p>}
          </ErrorMessage>
        </FormControl>
        {!showBusesFields ? (
          <Field
            name="fixedRate"
            component={Autocomplete}
            fullWidth
            getOptionLabel={(
              rate: RateModel['categories'][0]['fixedPrices'][0]
            ) => `${rate.name} - $${rate.price}`}
            noOptionsText="No se han encontrado resultados"
            loadingText="Cargando..."
            options={fixedRatesOptions}
            renderInput={(params: AutocompleteRenderInputParams) => (
              <TextFieldMaterial
                {...params}
                label="Tarifa fija"
                variant="outlined"
                InputProps={{
                  ...params.InputProps
                }}
                fullWidth
              />
            )}
          />
        ) : (
          <>
            <FormControl variant="outlined" fullWidth className="col-span-1">
              <InputLabel>Tipo de bus</InputLabel>
              <Field
                component={Select}
                name="bus_type_id"
                label="Tipo de bus"
                placeholder="Seleccione un tipo de bus"
                defaultValue={14}
              >
                {optionsBuses?.map(({ id: key, name }) => {
                  return (
                    <MenuItem value={key} key={key}>
                      <div className="flex items-center w-full">
                        <div className="w-8">
                          <FontAwesomeIcon
                            icon={faBus}
                            color="#8EC3E6"
                            size="1x"
                          />
                        </div>
                        <p className="m-0">{name}</p>
                      </div>
                    </MenuItem>
                  );
                })}
              </Field>
            </FormControl>
            <div className="flex gap-4">
              <Field
                name="bus_provider_price"
                label="Costo proveedor"
                variant="outlined"
                component={TextField}
                type="number"
              />
              <Field
                name="bus_customer_price"
                label="Costo cliente"
                variant="outlined"
                component={TextField}
                type="number"
              />
            </div>
            <div className="w-32">
              <Field
                component={CheckboxWithLabel}
                type="checkbox"
                name="bus_is_outsourced"
                color="primary"
                Label={{ label: 'Tercerizado' }}
              />
            </div>
            <Field
              name="bus_observation"
              label="Observaciones buses"
              variant="outlined"
              component={TextField}
              className="col-start-1 col-end-3"
              fullWidth
            />
          </>
        )}
      </div>
    </>
  );
};

export default VehicleSingleForm;